import "@/App.css";
import React, { useEffect, useRef, Component, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { CartProvider } from './context/CartContext';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import useAutoUpdate from './hooks/useAutoUpdate';
import AppGate from './components/AppGate';
import BusinessProfileGate from './components/BusinessProfileGate';
import AdminPreviewBar from './components/AdminPreviewBar';
import SettingsSyncBanner from './components/SettingsSyncBanner';
import { Toaster } from './components/ui/sonner';
// EAGER (rutas más calientes, evita FOUC del usuario final)
import ProductListPage from './pages/ProductListPage';
import CheckoutPage from './pages/CheckoutPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminLayout from './components/AdminLayout';
import AccountPage from './pages/AccountPage';
import PriceListPage from './pages/PriceListPage';

// LAZY (admin + secundarias — bundles separados, no se descargan hasta navegar)
const QuickOrderPage = lazy(() => import('./pages/QuickOrderPage'));
const AdminHomePage = lazy(() => import('./pages/AdminHomePage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const AdminBannersPage = lazy(() => import('./pages/AdminBannersPage'));
const AdminBannerLayoutPage = lazy(() => import('./pages/AdminBannerLayoutPage'));
const AdminComboBannerLayoutPage = lazy(() => import('./pages/AdminComboBannerLayoutPage'));
const AdminWhatsAppAgentPage = lazy(() => import('./pages/AdminWhatsAppAgentPage'));
const AdminBlogPage = lazy(() => import('./pages/AdminBlogPage'));
const AdminSurveysPage = lazy(() => import('./pages/AdminSurveysPage'));
const AdminCommerceRequestsPage = lazy(() => import('./pages/AdminCommerceRequestsPage'));
const AdminOnboardingPreviewPage = lazy(() => import('./pages/AdminOnboardingPreviewPage'));
const AdminWineClubPage = lazy(() => import('./pages/AdminWineClubPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const AdminCatalogLayoutPage = lazy(() => import('./pages/AdminCatalogLayoutPage'));
const BannerRenderPage = lazy(() => import('./pages/BannerRenderPage'));
const ComboBannerRenderPage = lazy(() => import('./pages/ComboBannerRenderPage'));
const CatalogRenderPage = lazy(() => import('./pages/CatalogRenderPage'));
const AdminClientesDuplicadosPage = lazy(() => import('./pages/AdminClientesDuplicadosPage'));
const AdminModulesPage = lazy(() => import('./pages/AdminModulesPage'));
const AdminReconciliationPage = lazy(() => import('./pages/AdminReconciliationPage'));
const AdminBroadcastPage = lazy(() => import('./pages/AdminBroadcastPage'));
const AdminShippingPage = lazy(() => import('./pages/AdminShippingPage'));
const AdminThemePage = lazy(() => import('./pages/AdminThemePage'));
const AdminStorePage = lazy(() => import('./pages/AdminStorePage'));
const AdminPointsPage = lazy(() => import('./pages/AdminPointsPage'));
const AdminClientesUnificadosPage = lazy(() => import('./pages/AdminClientesUnificadosPage'));
const AdminClientesIncompletosPage = lazy(() => import('./pages/AdminClientesIncompletosPage'));
const AdminCustomersPage = lazy(() => import('./pages/AdminCustomersPage'));
const AdminDiscountsPage = lazy(() => import('./pages/AdminDiscountsPage'));
const AdminPromotionsPage = lazy(() => import('./pages/AdminPromotionsPage'));
const AdminUpgradeRequestsPage = lazy(() => import('./pages/AdminUpgradeRequestsPage'));
const AdminSellersPage = lazy(() => import('./pages/AdminSellersPage'));
const AdminOrdersPage = lazy(() => import('./pages/AdminOrdersPage'));
const AdminCombosPage = lazy(() => import('./pages/AdminCombosPage'));
const AdminNotificationsPage = lazy(() => import('./pages/AdminNotificationsPage'));
const AdminMigrationPage = lazy(() => import('./pages/AdminMigrationPage'));
const AdminAnalyticsPage = lazy(() => import('./pages/AdminAnalyticsPage'));
const AdminAuditoriaPage = lazy(() => import('./pages/AdminAuditoriaPage'));
const StockAuditPage = lazy(() => import('./pages/StockAuditPage'));
const AdminPricingPage = lazy(() => import('./pages/AdminPricingPage'));
const AdminFacturadoresPage = lazy(() => import('./pages/AdminFacturadoresPage'));
const AdminExportPage = lazy(() => import('./pages/AdminExportPage'));
const FacturadorLayout = lazy(() => import('./pages/FacturadorLayout'));
const FacturadorLogin = lazy(() => import('./pages/FacturadorLayout').then(m => ({ default: m.FacturadorLogin })));
const AdminCuentasCorrientesPage = lazy(() => import('./pages/AdminCuentasCorrientesPage'));
const AdminProveedoresPage = lazy(() => import('./pages/AdminProveedoresPage'));
const AdminComprobantesPage = lazy(() => import('./pages/AdminComprobantesPage'));
const AdminRepartoPage = lazy(() => import('./pages/AdminRepartoPage'));
const AdminComprasPage = lazy(() => import('./pages/AdminComprasPage'));
const AdminVentasPage = lazy(() => import('./pages/AdminVentasPage'));
const AdminCondicionesPage = lazy(() => import('./pages/AdminCondicionesPage'));
const PaymentEntryPage = lazy(() => import('./pages/PaymentEntryPage'));
const AdminCajaPage = lazy(() => import('./pages/AdminCajaPage'));
const AccountStatementPage = lazy(() => import('./pages/AccountStatementPage'));
const SellerDashboardPage = lazy(() => import('./pages/SellerDashboardPage'));
const SellerRoutePage = lazy(() => import('./pages/SellerRoutePage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));

// Error Boundary to prevent yellow screens
class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null, info: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info);
    this.setState({ info });
  }
  render() {
    if (this.state.hasError) {
      const errMsg = (this.state.error && (this.state.error.message || String(this.state.error))) || 'Error desconocido';
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: '2rem' }}>
          <div style={{ textAlign: 'center', maxWidth: '480px' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1B1F3B', marginBottom: '0.5rem' }}>Algo salió mal</h2>
            <p style={{ color: '#64748B', marginBottom: '0.75rem', fontSize: '0.875rem' }}>Hubo un error inesperado. Intentá recargar.</p>
            <details style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', textAlign: 'left', marginBottom: '1rem' }}>
              <summary style={{ cursor: 'pointer', color: '#991b1b', fontSize: '0.75rem', fontWeight: 700 }}>Ver detalle técnico</summary>
              <pre style={{ fontSize: '0.7rem', color: '#7f1d1d', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: '0.5rem' }}>{errMsg}</pre>
            </details>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
              <button onClick={() => { this.setState({ hasError: false, error: null, info: null }); window.location.reload(); }}
                style={{ padding: '0.6rem 1.4rem', background: '#1B1F3B', color: 'white', borderRadius: '0.6rem', border: 'none', fontWeight: 700, cursor: 'pointer' }}>
                Recargar
              </button>
              <button onClick={() => { this.setState({ hasError: false, error: null, info: null }); window.location.href = '/products'; }}
                style={{ padding: '0.6rem 1.4rem', background: 'white', color: '#1B1F3B', borderRadius: '0.6rem', border: '1px solid #cbd5e1', fontWeight: 700, cursor: 'pointer' }}>
                Volver al inicio
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Auth callback component to handle OAuth redirect
const AuthCallback = () => {
  const { exchangeSession } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      // Clear hash immediately to prevent re-processing on re-render
      window.history.replaceState(null, '', window.location.pathname);
      
      exchangeSession(sessionId)
        .then(() => {
          navigate('/products', { replace: true });
        })
        .catch((error) => {
          console.error('Auth error:', error);
          // Retry once after a short delay
          setTimeout(() => {
            exchangeSession(sessionId)
              .then(() => navigate('/products', { replace: true }))
              .catch(() => navigate('/products', { replace: true }));
          }, 1000);
        });
    } else {
      navigate('/products', { replace: true });
    }
  }, [exchangeSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
        <p className="text-slate-600">Iniciando sesión...</p>
      </div>
    </div>
  );
};

// Customer type is handled entirely by AppGate's RegistrationForm
// No separate modal needed

// Main router component
const AppRouter = () => {
  const location = useLocation();

  useEffect(() => {
    // Aggressively remove any injected external badges
    const removeBadges = () => {
      document.querySelectorAll(
        'body > a[style*="position: fixed"], body > a[style*="z-index: 9999"], body > a[style*="border-radius: 50px"], a[id*="badge"], a[href*="emergent"], div[class*="emergent"], div[id*="emergent"]'
      ).forEach(el => el.remove());
    };
    removeBadges();
    // Watch for dynamically injected elements
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1) {
            const el = node;
            const style = el.getAttribute?.('style') || '';
            const href = el.getAttribute?.('href') || '';
            const id = el.id || '';
            if (
              (el.tagName === 'A' && (style.includes('position: fixed') || style.includes('z-index: 9999') || style.includes('border-radius: 50px'))) ||
              href.includes('emergent') || id.includes('badge') || id.includes('emergent')
            ) {
              el.remove();
            }
          }
        }
      }
    });
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, []);

  // Check URL fragment for session_id - handle OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Suspense fallback={<div style={{minHeight:'60vh',display:'flex',alignItems:'center',justifyContent:'center',color:'#94a3b8',fontSize:14}}>Cargando…</div>}>
      <Routes>
        {/* Banner render aislado (para screenshot server-side con Playwright) */}
        <Route path="/banner-render/:productId" element={<BannerRenderPage />} />
        <Route path="/combo-banner-render/:comboId" element={<ComboBannerRenderPage />} />
        <Route path="/catalog-render" element={<CatalogRenderPage />} />
        {/* Admin routes - no gate */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/dashboard" element={<AdminLayout><AdminHomePage /></AdminLayout>} />
        <Route path="/admin/products" element={<AdminLayout><AdminDashboardPage /></AdminLayout>} />
        <Route path="/admin/banners" element={<AdminLayout><AdminBannersPage /></AdminLayout>} />
        <Route path="/admin/banner-layout" element={<AdminLayout><AdminBannerLayoutPage /></AdminLayout>} />
        <Route path="/admin/combo-banner-layout" element={<AdminLayout><AdminComboBannerLayoutPage /></AdminLayout>} />
        <Route path="/admin/whatsapp-agent" element={<AdminLayout><AdminWhatsAppAgentPage /></AdminLayout>} />
        <Route path="/admin/blog" element={<AdminLayout><AdminBlogPage /></AdminLayout>} />
        <Route path="/admin/surveys" element={<AdminLayout><AdminSurveysPage /></AdminLayout>} />
        <Route path="/admin/commerce-requests" element={<AdminLayout><AdminCommerceRequestsPage /></AdminLayout>} />
        <Route path="/admin/preview-onboarding" element={<AdminLayout><AdminOnboardingPreviewPage /></AdminLayout>} />
        <Route path="/admin/wine-club" element={<AdminLayout><AdminWineClubPage /></AdminLayout>} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPage />} />
        <Route path="/admin/catalog-layout" element={<AdminLayout><AdminCatalogLayoutPage /></AdminLayout>} />
        <Route path="/admin/reconciliacion" element={<AdminLayout><AdminReconciliationPage /></AdminLayout>} />
        <Route path="/admin/broadcast" element={<AdminLayout><AdminBroadcastPage /></AdminLayout>} />
        <Route path="/admin/shipping" element={<AdminLayout><AdminShippingPage /></AdminLayout>} />
        <Route path="/admin/theme" element={<AdminLayout><AdminThemePage /></AdminLayout>} />
        <Route path="/admin/store" element={<AdminLayout><AdminStorePage /></AdminLayout>} />
        <Route path="/admin/points" element={<AdminLayout><AdminPointsPage /></AdminLayout>} />
        <Route path="/admin/customers" element={<AdminLayout><AdminCustomersPage /></AdminLayout>} />
        <Route path="/admin/discounts" element={<AdminLayout><AdminDiscountsPage /></AdminLayout>} />
        <Route path="/admin/promotions" element={<AdminLayout><AdminPromotionsPage /></AdminLayout>} />
        <Route path="/admin/upgrade-requests" element={<AdminLayout><AdminUpgradeRequestsPage /></AdminLayout>} />
        <Route path="/admin/sellers" element={<AdminLayout><AdminSellersPage /></AdminLayout>} />
        <Route path="/admin/orders" element={<AdminLayout><AdminOrdersPage /></AdminLayout>} />
        <Route path="/admin/combos" element={<AdminLayout><AdminCombosPage /></AdminLayout>} />
        <Route path="/admin/notifications" element={<AdminLayout><AdminNotificationsPage /></AdminLayout>} />
        <Route path="/admin/erp/migracion" element={<AdminLayout><AdminMigrationPage /></AdminLayout>} />
        <Route path="/admin/erp/cuentas-corrientes" element={<AdminLayout><AdminCuentasCorrientesPage /></AdminLayout>} />
        <Route path="/admin/erp/proveedores" element={<AdminLayout><AdminProveedoresPage /></AdminLayout>} />
        <Route path="/admin/erp/comprobantes" element={<AdminLayout><AdminComprobantesPage /></AdminLayout>} />
        <Route path="/admin/erp/reparto" element={<AdminLayout><AdminRepartoPage /></AdminLayout>} />
        <Route path="/admin/erp/compras" element={<AdminLayout><AdminComprasPage /></AdminLayout>} />
        <Route path="/admin/erp/ventas" element={<AdminLayout><AdminVentasPage /></AdminLayout>} />
        <Route path="/admin/erp/cobros" element={<AdminLayout><PaymentEntryPage mode="customer" /></AdminLayout>} />
        <Route path="/admin/erp/pagos" element={<AdminLayout><PaymentEntryPage mode="supplier" /></AdminLayout>} />
        <Route path="/admin/erp/condiciones" element={<AdminLayout><AdminCondicionesPage /></AdminLayout>} />
        <Route path="/admin/erp/caja" element={<AdminLayout><AdminCajaPage /></AdminLayout>} />
        <Route path="/admin/erp/resumen-cliente" element={<AdminLayout><AccountStatementPage kind="customer" /></AdminLayout>} />
        <Route path="/admin/erp/resumen-proveedor" element={<AdminLayout><AccountStatementPage kind="supplier" /></AdminLayout>} />
        <Route path="/admin/erp/analytics" element={<AdminLayout><AdminAnalyticsPage /></AdminLayout>} />
        <Route path="/admin/erp/auditoria" element={<AdminLayout><AdminAuditoriaPage /></AdminLayout>} />
        <Route path="/admin/erp/stock-audit" element={<AdminLayout><StockAuditPage /></AdminLayout>} />
        <Route path="/admin/clientes-unificados" element={<AdminLayout><AdminClientesUnificadosPage /></AdminLayout>} />
        <Route path="/admin/clientes-incompletos" element={<AdminLayout><AdminClientesIncompletosPage /></AdminLayout>} />
        <Route path="/admin/clientes-duplicados" element={<AdminLayout><AdminClientesDuplicadosPage /></AdminLayout>} />
        <Route path="/admin/modules" element={<AdminLayout><AdminModulesPage /></AdminLayout>} />
        <Route path="/admin/erp/pricing" element={<AdminLayout><AdminPricingPage /></AdminLayout>} />
        <Route path="/admin/erp/facturadores" element={<AdminLayout><AdminFacturadoresPage /></AdminLayout>} />
        <Route path="/admin/erp/export" element={<AdminLayout><AdminExportPage /></AdminLayout>} />

        {/* === Sección Facturador (rol restringido) === */}
        <Route path="/facturador/login" element={<FacturadorLogin />} />
        <Route path="/facturador" element={<FacturadorLayout />}>
          <Route index element={<Navigate to="pos" replace />} />
          <Route path="pos" element={<AdminVentasPage />} />
          <Route path="cobros" element={<PaymentEntryPage mode="customer" />} />
          <Route path="comprobantes" element={<AdminComprobantesPage />} />
          <Route path="caja" element={<AdminCajaPage />} />
          <Route path="stock-audit" element={<StockAuditPage />} />
        </Route>

        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
        
        {/* Seller routes */}
        <Route path="/seller" element={<AppGate><SellerDashboardPage /></AppGate>} />
        <Route path="/seller/route" element={<AppGate><SellerRoutePage /></AppGate>} />
        
        {/* Public routes - protected by AppGate */}
        <Route path="/" element={<Navigate to="/products" replace />} />
        <Route path="/products" element={<AppGate><ProductListPage /></AppGate>} />
        <Route path="/quick-order" element={<AppGate><QuickOrderPage /></AppGate>} />
        <Route path="/checkout" element={<AppGate><CheckoutPage /></AppGate>} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacidad" element={<PrivacyPolicyPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/mi-cuenta/lista-precios" element={<PriceListPage />} />
        <Route path="/lista-precios" element={<PriceListPage />} />
        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/products" replace />} />
      </Routes>
      </Suspense>
    </>
  );
};

function App() {
  // Auto-update: checks for new version and reloads automatically
  useAutoUpdate();

  // Remove ANY badge/link from the hosting provider that may get injected at
  // runtime (defensive: complements the CSS rules in index.css). Idempotent.
  useEffect(() => {
    const remove = () => {
      try {
        const sel = [
          'a[href*="emergent.sh"]', 'a[href*="emergent.com"]',
          'div[id*="emergent-badge"]', 'div[class*="emergent-badge"]',
          'iframe[src*="emergent"]',
        ].join(',');
        document.querySelectorAll(sel).forEach((n) => {
          // Solo eliminar si está pegado al body (badge/banner inyectado),
          // no si es contenido legítimo del app.
          if (n.parentElement === document.body) n.remove();
        });
      } catch (e) { /* ignore */ }
    };
    remove();
    const obs = new MutationObserver(remove);
    obs.observe(document.body, { childList: true, subtree: false });
    return () => obs.disconnect();
  }, []);
  
  return (
    <ThemeProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <AppRouter />
              <BusinessProfileGate />
              <AdminPreviewBar />
              <SettingsSyncBanner />
              <Toaster position="top-center" richColors closeButton />
            </ErrorBoundary>
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
